/**
 * Resource registry for the Super Admin module.
 *
 * Same shape as `pages/business/resources.js`; see that file for the
 * full description of how each entry is consumed by the generic
 * ResourceList / ResourceForm pages.
 */
import { SuperAdminAPI, UsersAPI } from '../../lib/api';

// Common loaders -----------------------------------------------------------

const loadOrganisations = () =>
  SuperAdminAPI.organisations.list({ page_size: 500 }).then((d) => d.results || d);

const loadRoles = () =>
  SuperAdminAPI.roles.list({ page_size: 500 }).then((d) => d.results || d);

const loadPermissions = () =>
  SuperAdminAPI.permissions.list({ page_size: 500 }).then((d) => d.results || d);

const loadHierarchies = () =>
  SuperAdminAPI.hierarchies.list({ page_size: 500 }).then((d) => d.results || d);

const loadUsers = () =>
  UsersAPI.list({ page_size: 500 }).then((d) => d.results || d);

// Resource definitions -----------------------------------------------------
export const SUPERADMIN_RESOURCES = {
  organisations: {
    slug: 'organisations',
    label: 'Organisations',
    singular: 'Organisation',
    icon: '🏢',
    api: SuperAdminAPI.organisations,
    detailsLock: true,
    listHint:
      'Lock an organisation with a PIN to freeze its name, code, address, plan and status. ' +
      'People in that organisation keep working while it is locked. Unlocking asks for the same PIN.',
    columns: [
      { key: 'id',                  label: 'ID' },
      { key: 'name',                label: 'Name' },
      { key: 'sub_domain',          label: 'Sub-domain' },
      { key: 'plan_type',           label: 'Plan' },
      { key: 'subscription_status', label: 'Subscription' },
      { key: 'tenant_status',       label: 'Tenant' },
      { key: 'is_active',           label: 'Active', format: 'bool' },
      { key: 'is_public',           label: 'Public org', format: 'bool' },
      { key: 'details_locked',      label: 'Details', format: 'lock' },
    ],
    fields: [
      { name: 'name',       label: 'Name', type: 'text', required: true },
      { name: 'sub_domain', label: 'Organisation Code', type: 'text', required: true,
        placeholder: 'Enter Organisation Code eg: Apple',
        help: 'Lower-case identifier; becomes the tenant schema name.' },
      { name: 'city',       label: 'City',  type: 'text' },
      { name: 'state',      label: 'State', type: 'text' },
      { name: 'address',    label: 'Address', type: 'textarea' },
      { name: 'no_of_hierarchy_level', label: '# hierarchy levels', type: 'number' },
      { name: 'plan_type',  label: 'Plan type', type: 'select', options: [
        { value: 'trail',    label: 'Trail' },
        { value: 'business', label: 'Business' },
        { value: 'premium',  label: 'Premium' },
      ]},
      { name: 'subscription_status', label: 'Subscription status', type: 'select', options: [
        { value: 'active',    label: 'Active' },
        { value: 'cancelled', label: 'Cancelled' },
      ]},
      { name: 'billing_type', label: 'Billing', type: 'select', options: [
        { value: 'monthly', label: 'Monthly' },
        { value: 'yearly',  label: 'Yearly' },
      ]},
      { name: 'trail_start_date', label: 'Trail start', type: 'date' },
      { name: 'trail_end_date',   label: 'Trail end',   type: 'date' },
      { name: 'charged_amount',   label: 'Charged amount', type: 'number', step: '0.01' },
      { name: 'approved',  label: 'Approved',  type: 'checkbox' },
      { name: 'is_active', label: 'Active',    type: 'checkbox', default: true },
    ],
    canDelete: (row) => !row.is_public,
    rowActions: (row) => ([
      { label: 'Modules', to: `/super-admin/organisations/${row.id}/modules` },
      row.is_public ? { label: 'Dashboard', to: '/business' } : null,
    ].filter(Boolean)),
  },

  users: {
    slug: 'users',
    label: 'Users',
    singular: 'User',
    icon: '👤',
    api: SuperAdminAPI.users,
    columns: [
      { key: 'id',         label: 'ID' },
      { key: 'email',      label: 'Email' },
      { key: 'full_name',  label: 'Name' },
      { key: 'role_name',  label: 'Role' },
      { key: 'is_active',  label: 'Active', format: 'bool' },
      { key: 'is_admin',   label: 'Admin',  format: 'bool' },
      { key: 'is_superuser', label: 'Super', format: 'bool' },
    ],
    fields: [
      // ----- IDENTITY ------------------------------------------------
      { name: 'first_name',   label: 'First name', type: 'text' },
      { name: 'last_name',    label: 'Last name',  type: 'text' },
      { name: 'email',        label: 'Email',      type: 'email', required: true },
      { name: 'username',     label: 'Username',   type: 'text' },
      { name: 'phone_number', label: 'Phone',      type: 'tel' },

      // ----- ORGANISATION (server-side searchable) -------------------
      //
      // Required: the super-admin must explicitly pick the tenant
      // schema the new user will live in.  The backend
      // (``UserViewSet.create``) resolves this id in the public schema,
      // then provisions the user INSIDE ``schema_context(org.schema_name)``
      // so the row lands in the correct tenant — see
      // ``super_admin/api_views.py``.
      //
      // Only available at create-time: changing a user's organisation
      // would mean moving them between tenant schemas, which is a
      // separate operation we don't expose from this generic form.
      {
        name: 'organisation',
        label: 'Organisation',
        type: 'org-autocomplete',
        required: true,
        onlyOnCreate: true,
        placeholder: 'Search organisations by name or sub-domain…',
        help:
          'The user will be created inside this organisation’s tenant schema. ' +
          'Type to search; up to 10 matches load per query.',
      },

      { name: 'role',         label: 'Role',       type: 'select', loader: loadRoles, optionLabel: (r) => r.name },
      { name: 'hierarchy',    label: 'Hierarchy',  type: 'select', loader: loadHierarchies, optionLabel: (h) => h.name },
      { name: 'supervisor',   label: 'Supervisor', type: 'select', loader: loadUsers,
        optionLabel: (u) => `${u.first_name || ''} ${u.last_name || ''} (${u.email})`.trim() },
      { name: 'hr',           label: 'HR',         type: 'select', loader: loadUsers,
        optionLabel: (u) => `${u.first_name || ''} ${u.last_name || ''} (${u.email})`.trim() },

      { name: 'city',         label: 'City',       type: 'text' },
      { name: 'state',        label: 'State',      type: 'text' },
      { name: 'address',      label: 'Address',    type: 'textarea' },
      { name: 'is_active',    label: 'Active',     type: 'checkbox', default: true },
      { name: 'is_admin',     label: 'Org admin',  type: 'checkbox' },
      { name: 'is_admin_approved', label: 'Admin approved', type: 'checkbox' },
      { name: 'is_complaints_admin', label: 'Complaints admin', type: 'checkbox' },
      // The password field is only useful at create-time; the generic
      // form sends whatever is filled in.  For existing users use the
      // dedicated "Set password" action on the detail row.
      { name: 'password', label: 'Password (only when creating)', type: 'password',
        onlyOnCreate: true },
    ],
  },

  roles: {
    slug: 'roles',
    label: 'Roles',
    singular: 'Role',
    icon: '🎭',
    api: SuperAdminAPI.roles,
    columns: [
      { key: 'id',                label: 'ID' },
      { key: 'name',              label: 'Name' },
      { key: 'organisation_name', label: 'Organisation' },
    ],
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
    ],
  },

  permissions: {
    slug: 'permissions',
    label: 'Permissions',
    singular: 'Permission',
    icon: '🔐',
    api: SuperAdminAPI.permissions,
    columns: [
      { key: 'id',      label: 'ID' },
      { key: 'name',    label: 'Name' },
      { key: 'create',  label: 'Create',  format: 'bool' },
      { key: 'read',    label: 'Read',    format: 'bool' },
      { key: 'update',  label: 'Update',  format: 'bool' },
      { key: 'destroy', label: 'Destroy', format: 'bool' },
    ],
    fields: [
      { name: 'name',    label: 'Name', type: 'text', required: true },
      { name: 'create',  label: 'Create',  type: 'checkbox' },
      { name: 'read',    label: 'Read',    type: 'checkbox' },
      { name: 'update',  label: 'Update',  type: 'checkbox' },
      { name: 'destroy', label: 'Destroy', type: 'checkbox' },
    ],
  },

  'role-permissions': {
    slug: 'role-permissions',
    label: 'Role Permissions',
    singular: 'Role-Permission',
    icon: '🪝',
    api: SuperAdminAPI.rolePermissions,
    columns: [
      { key: 'id',              label: 'ID' },
      { key: 'role_name',       label: 'Role' },
      { key: 'permission_name', label: 'Permission' },
    ],
    fields: [
      { name: 'role',       label: 'Role',       type: 'select', required: true, loader: loadRoles,       optionLabel: (r) => r.name },
      { name: 'permission', label: 'Permission', type: 'select', required: true, loader: loadPermissions, optionLabel: (p) => p.name },
    ],
  },

  hierarchies: {
    slug: 'hierarchies',
    label: 'Hierarchies',
    singular: 'Hierarchy',
    icon: '📐',
    api: SuperAdminAPI.hierarchies,
    columns: [
      { key: 'id',   label: 'ID' },
      { key: 'name', label: 'Name' },
      { key: 'rank', label: 'Rank' },
    ],
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'rank', label: 'Rank', type: 'number' },
      { name: 'hierarchy', label: 'Parent hierarchy', type: 'select',
        loader: loadHierarchies, optionLabel: (h) => h.name },
    ],
  },

  modules: {
    slug: 'modules',
    label: 'Modules',
    singular: 'Module',
    icon: '🧩',
    api: SuperAdminAPI.modules,
    columns: [
      { key: 'id',        label: 'ID' },
      { key: 'name',      label: 'Name' },
      { key: 'is_active', label: 'Active', format: 'bool' },
      { key: 'default',   label: 'Default', format: 'bool' },
    ],
    fields: [
      { name: 'name',      label: 'Name', type: 'text', required: true },
      { name: 'is_active', label: 'Active',  type: 'checkbox', default: true },
      { name: 'default',   label: 'Default', type: 'checkbox' },
    ],
  },

  controls: {
    slug: 'controls',
    label: 'Controls',
    singular: 'Control',
    icon: '🎛️',
    api: SuperAdminAPI.controls,
    columns: [
      { key: 'id',            label: 'ID' },
      { key: 'control_key',   label: 'Key' },
      { key: 'control_name',  label: 'Name' },
      { key: 'control_value', label: 'Enabled', format: 'bool' },
    ],
    fields: [
      { name: 'control_key',   label: 'Key',  type: 'text', required: true },
      { name: 'control_name',  label: 'Name', type: 'text', required: true },
      { name: 'control_value', label: 'Enabled', type: 'checkbox', default: true },
    ],
  },
};

/** Ordered list used by the dashboard tiles. */
export const SUPERADMIN_RESOURCE_ORDER = [
  'organisations', 'users', 'roles', 'permissions', 'role-permissions',
  'hierarchies',   'modules', 'controls',
];
